import {
    BoundedContent,
    Box,
    Carousel,
    CarouselSlide,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    H3,
    Link,
    Row,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    responsive,
    useScreenClass,
} from '@vp/swan';
import { StaticImageData } from 'next/image';
import { DS_BRIEF_CREATE_URL, __, __product, getCareBriefCreateURL } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { sendNavigationEvent } from '@99designs/tracking';
import { products } from './products';
import type { MoreProductsProduct } from '../../types';

const ResponsiveFlexBox = responsive(FlexBox);

interface MoreProductsProps {
    locale: string;
}

export const MoreProducts = ({ locale }: MoreProductsProps) => {
    const allProducts = products();

    const availableProducts = allProducts.filter(({ mpv }) => {
        return mpv.length > 0;
    });

    const hasProducts = availableProducts.length > 0;
    if (hasProducts) {
        return (
            <Box marginBottom={'7'} data-testid="more-products">
                <BoundedContent>
                    <MoreProductsHeader />
                    <ScrollableProductsList products={availableProducts} locale={locale} />
                </BoundedContent>
            </Box>
        );
    }

    return (
        <Box marginBottom={'7'} data-testid="more-products">
            <BoundedContent>
                <FlexBox justifyContent="center" mb={10}>
                    <AllProductsLink />
                </FlexBox>
            </BoundedContent>
        </Box>
    );
};

function MoreProductsHeader() {
    return (
        <GridContainer>
            <Row marginTop="between-sections">
                <Column
                    offset={1}
                    span={9}
                    offsetLg={0}
                    spanLg={9}
                    offsetMd={0}
                    spanMd={9}
                    offsetSm={0}
                    spanSm={8}
                >
                    <H3 fontSkin="title-section" fontSize="x2large">
                        {__('More design options')}
                    </H3>
                </Column>
                <Column span={2} spanLg={3} spanMd={3} spanSm={4}>
                    <ResponsiveFlexBox
                        xs={{ justifyContent: 'flex-start', marginTop: 4 }}
                        sm={{ justifyContent: 'flex-end', marginTop: 0 }}
                    >
                        <AllProductsLink />
                    </ResponsiveFlexBox>
                </Column>
            </Row>
        </GridContainer>
    );
}

function ScrollableProductsList({
    products,
    locale,
}: {
    products: MoreProductsProduct[];
    locale: string;
}) {
    const { pageData } = usePage();
    const screenClass = useScreenClass();

    const propsByScreenClass = {
        xs: { slidesToShow: 3 },
        sm: { slidesToShow: 5 },
        md: { slidesToShow: products.length },
        lg: { slidesToShow: products.length },
        xl: { slidesToShow: products.length },
    };

    return (
        <Carousel
            accessibleTextForPrevious={__('Go to previous slide')}
            accessibleTextForNext={__('Go to next slide')}
            {...propsByScreenClass[screenClass]}
        >
            {products.map(({ image, title, mpv, trackingData }, index) => (
                <CarouselSlide key={mpv} data-testid="product-link" paddingX={'5'}>
                    <StandardTile
                        skin="circular"
                        render={({ className, children }) => (
                            <Link
                                className={className}
                                href={`/design/get-started?mpvId=${mpv}&locale=${locale}`}
                                title={`Design Services Brief - ${title}`} // TODO i18n missing translation
                                onClick={() =>
                                    sendNavigationEvent({ ...trackingData, ...pageData })
                                }
                                skin="unstyled"
                                data-section={trackingData.label}
                                data-position={index + 1}
                                data-translation={trackingData.navigationDetail}
                                data-testid={`product-link-${index}`}
                            >
                                {children}
                            </Link>
                        )}
                    >
                        <StandardTileImage>
                            <FluidImage
                                src={(image as unknown as StaticImageData).src}
                                alt=""
                                role="presentation"
                            />
                        </StandardTileImage>
                        <StandardTileContents>
                            <StandardTileName>{title}</StandardTileName>
                        </StandardTileContents>
                    </StandardTile>
                </CarouselSlide>
            ))}
        </Carousel>
    );
}

const AllProductsLinkEvent = {
    label: 'Content:Design Option Link',
    eventDetail: `/design;${DS_BRIEF_CREATE_URL}/care-design?mpvId=${__product(
        'mpv-id-business-card'
    )};Design Option Link;AllDesignOptions-CTA`,
    navigationDetail: 'AllDesignOptions-CTA',
    category: 'Navigation Clicked',
};

function AllProductsLink() {
    const { pageData } = usePage();
    const onClickTracking = () => {
        sendNavigationEvent({ ...AllProductsLinkEvent, ...pageData });
    };

    return (
        <Link
            skin="cta"
            onClick={onClickTracking}
            data-section={AllProductsLinkEvent.label}
            data-position={0}
            data-translation={AllProductsLinkEvent.navigationDetail}
            href={getCareBriefCreateURL()}
        >
            {__('See all design options')}
        </Link>
    );
}
